/**
 * Generic, theme-aware modal dialog.
 *
 * Extracted from the AccountModal overlay pattern (`AccountModal.tsx`):
 * backdrop blur + click-to-close, Esc-to-close, body scroll lock, and the
 * CSS-variable idiom (`--bg`, `--ink`, `--border`, `--accent`) so it renders
 * correctly in both light and `.dark`. Reused by every edit/add form
 * across the DataTable-driven pages.
 *
 * `ponytail:` no focus-trap yet — the browser tab order can reach the page
 * behind the overlay. Add a focus-trap when a modal hosts frequent keyboard
 * entry (e.g. a wizard); today the forms are short and Esc/click-close is enough.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { lockBody, unlockBody } from '../lib/body-lock';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Width preset. sm=420, md=560, lg=720 (clamped to viewport). */
  size?: 'sm' | 'md' | 'lg';
  /** Accessible label (falls back to title text). */
  ariaLabel?: string;
}

const WIDTHS: Record<NonNullable<ModalProps['size']>, number> = {
  sm: 420,
  md: 560,
  lg: 720,
};

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md', ariaLabel }: ModalProps) {
  // Callers pass inline onClose arrows (new identity each render). Keep it in a
  // ref so the Esc/scroll-lock effect depends only on `open` and isn't torn
  // down + rebound on every parent render while the modal is open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc-to-close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    lockBody();
    return () => {
      window.removeEventListener('keydown', onKey);
      unlockBody();
    };
  }, [open]);

  if (!open) return null;

  const label = typeof title === 'string' ? title : ariaLabel;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/45 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <div
        className="relative w-full my-8 flex flex-col rounded-xl shadow-2xl border border-border bg-card text-card-foreground"
        style={{ maxWidth: `min(${WIDTHS[size]}px, calc(100vw - 2rem))` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold leading-tight">{title}</h2>
            {subtitle && <p className="text-xs mt-1 text-muted-foreground">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
